import { useCrmStore } from './crm'
import { usePlanStore } from './plan'
import { applySessionTheme } from './sessionTheme'
import { PriceBook } from '@/classes/PriceBook'
import { Session } from '@/classes/Session'
import CrmAppointmentImporter from '@/classes/importers/crmAppointmentImporter'
import SalesforceAppointmentImporter from '@/classes/importers/salesforceAppointmentImporter'
import { PlanPacketStatus } from '@/enums/PlanPacketStatus'
import { useRequestQueue } from '@/mixins/requestQueue'
import { windowRequestQueueConfig } from '@/mixins/requestQueueWindowConfig'
import Router from '@/router'
import type { Contact } from '@/services/CloudSync/cloudSyncPlanService'
import { useCloudSyncService } from '@/services/CloudSync/cloudSyncService'
import { isSameContact, planToContact } from '@/services/contactHelper'
import { deepToRaw } from '@/services/deepToRaw'
import { Feature, useFeatureService } from '@/services/featureService'
import { clearSavedFoxitSignatures } from '@/services/foxitSignatures'
import { planV0ExtractMigration } from '@/services/migrations/planV0ExtractMigration'
import { planV1EncryptMigration } from '@/services/migrations/planV1EncryptMigration'
import { useContentStore } from '@/stores/content'
import type {
	CombinedFhLocation,
	PlanData,
	SessionData,
	StoredSessionData,
} from '@/types'
import { captureException } from '@sentry/vue'
import { asyncComputed } from '@vueuse/core'
import localforageLib from 'localforage'
import { DateTime } from 'luxon'
import { defineStore } from 'pinia'
import { computed, ref, toRaw, watch } from 'vue'
import type { NavigationFailure } from 'vue-router'

export const localforage = localforageLib.createInstance({
	name: 'sessions',
	storeName: 'sessions',
})

export const useSessionStore = defineStore('session', () => {
	const content = useContentStore()
	const planStore = usePlanStore()
	const cloudSyncService = useCloudSyncService()
	const requestQueue = useRequestQueue('sessions', windowRequestQueueConfig)
	const activeSession = ref<Session>()
	const sessions = ref<Session[]>([])

	/** Price book override for testing */
	const testPriceBook = ref<PriceBook>()

	/** Get plans for the active session */
	const activePlans = computed(() => {
		return activeSession.value
			? planStore.getPlans(activeSession.value.planIds)
			: []
	})

	const activeSlideDeck = computed(() => {
		return activeSession.value
			? content.findDeck(
					activeSession.value.slideDeckId,
					activeSession.value.language
				)
			: undefined
	})

	watch(
		() => ({
			session: activeSession.value,
			deck: activeSlideDeck.value,
		}),
		({ session, deck }) => {
			// If not in presentation / documents don't apply theme
			if (!session || !deck?.content) {
				applySessionTheme(undefined)
				return
			}

			// Otherwise apply theme from Storyblok
			applySessionTheme({
				fontPrimary: deck.content.fontPrimary,
				fontSecondary: deck.content.fontSecondary,
				colorPrimary: deck.content.colorPrimaryBase,
				colorSecondary: deck.content.colorSecondaryBase,
				colorAccent: deck.content.colorAccentBase,
				colorBackground: deck.content.colorBackgroundBase,
				customCss: deck.content.customCss,
			})
		},
		{ immediate: true }
	)

	const activeFHGroupMedia = computed(() => {
		return activeSession.value
			? content.findFHGroupMedia(
					activeSession.value.FHGroupMediaId,
					activeSession.value.language
				)
			: undefined
	})

	function gotoSession(
		sessionId: string
	): Promise<void | NavigationFailure | undefined> {
		return Router.push({ name: 'present', params: { sessionId } })
	}

	/** Run all data migrations */
	async function migrateSessions(): Promise<void> {
		await planV0ExtractMigration()
		await planV1EncryptMigration()
	}

	/**
	 * Load session data from local storage.
	 * Call once to boot up the store when the app loads.
	 */
	async function loadAll(): Promise<void> {
		// Load all sessions from the new store
		const allSessions: Session[] = []

		await new Promise<void>((resolve) => {
			localforage.iterate(
				(sessionData: SessionData) => {
					try {
						const session = Session.deserialize(sessionData)
						allSessions.push(session)
					} catch (error) {
						console.error('Failed to load session data:', error)
					}
				},
				() => {
					sessions.value = allSessions
					resolve()
				}
			)
		})
	}

	function getSessionForAppointment(apptId: string) {
		return sessions.value?.find(
			(session) =>
				session.appointmentId === apptId ||
				session.salesforceEventAppointmentId === apptId
		)
	}

	function getSessionById(sessionId: string): Session | undefined {
		return sessions.value?.find((session) => session.id === sessionId)
	}

	/** Create a new session for the given lead and begin the presentation */
	async function startSession(session: Session) {
		if (activeSession.value) {
			throw Error("Can't start a new session when one is in progress")
		}

		// Add to list in memory
		sessions.value?.push(session)

		// Record timestamp
		session.startAt = DateTime.now().toISO()

		// Save locally
		await saveSession(session)
	}

	/** Resume the given presentation session */
	async function resumeSession(session: Session) {
		if (activeSession.value) {
			throw new Error(
				"Can't resume a session when a session is already active."
			)
		}

		// Start over from the beginning
		session.currentSlideIndex = 0

		await saveSession(session)
		await gotoSession(session.id)
	}

	/** Load a session from storage by id. Set it as the active session. */
	async function loadSession(sessionId: string) {
		const loaded = await localforage.getItem<StoredSessionData>(sessionId)

		if (loaded === null) {
			throw new Error("Can't load session, id not found: " + sessionId)
		}

		// Load all plans for this session
		const session = Session.deserialize(loaded)
		await planStore.setActivePlanIds(session.planIds)

		activeSession.value = session
	}

	/** Commit the active session to local storage */
	async function saveActiveSession() {
		if (!activeSession.value) {
			throw new Error("Can't save active session: it is undefined")
		}

		// Sync the in-memory session with the updated active session by id
		sessions.value = sessions.value.map((storedSession) =>
			storedSession.id === activeSession.value!.id
				? activeSession.value!
				: storedSession
		)

		await saveSession(activeSession.value)
	}

	/** Commit the given session to local storage */
	async function saveSession(session: Session) {
		const rawSession: SessionData = deepToRaw(toRaw(session.serialize()))

		try {
			await localforage.setItem(session.id, rawSession)
		} catch (e) {
			captureException(e, {
				extra: {
					message: `Error saving session.`,
				},
			})
		}

		await cloudSyncService.session.registerForUpsert(session.id)
	}

	/** Get a list of all the session ids */
	async function getSavedSessionIds(): Promise<string[]> {
		return await localforage.keys()
	}

	const activeFh = computed(() => {
		const crm = useCrmStore()
		return activeSession.value?.FuneralHomeLocationId
			? crm.findFuneralHome(activeSession.value?.FuneralHomeLocationId)
			: undefined
	})

	const activeOapProvider = computed(() => activeFh.value?.oap?.data.carrier)

	const activeInsuranceCarrier = computed(
		() => activeFh.value?.insuranceProduct?.carrier
	)

	const activeFhMedia = asyncComputed<CombinedFhLocation | undefined>(
		async () => {
			const fh = activeFh.value

			const fhMedia = await content.getCombinedFhLocation(
				fh!,
				activeSession.value!.language
			)

			return fhMedia
		}
	)

	/** Computed for the price book for this session */
	const activePriceBook = computed<PriceBook | undefined>(() => {
		if (testPriceBook.value) return testPriceBook.value

		const crm = useCrmStore()
		return activeFh.value
			? crm.findPriceBookForFuneralHome(activeFh.value.id)
			: undefined
	})

	const activePriceBookVersion = computed(() => {
		return activePriceBook.value?.data.name.includes('!!v2!!') ? 2 : 2
	})

	/** Delete everything from memory and local storage */
	async function purge(): Promise<any> {
		clearSavedFoxitSignatures()
		activeSession.value = undefined
		sessions.value = []

		// Also clear the plans store
		await planStore.purge()

		return localforage.clear()
	}

	/** This function retrieves the plan holder data given a plan ID from the active session **/
	function getPlanHolder(id: string) {
		return planStore.getPlan(id)
	}

	// Used to get the first plan holder that is not submitted
	function getFirstPlanHolderNotConfirmed() {
		return activePlans.value?.find(
			(plan) => plan.packetStatus === PlanPacketStatus.Incomplete
		)
	}

	// Used to return a session from the given PlanHolderId
	const getSessionFromPlanHolderId = (planHolderId: string) => {
		return sessions.value.find((session) =>
			session.planIds.some((planId) => planId === planHolderId)
		)
	}

	// This function below is used in AddOrRemovePersonModal
	const getPlanPrototype = (): Partial<PlanData> => {
		return {
			leadId: activePlans.value[0]?.leadId,
			opportunityId: activePlans.value[0]?.opportunityId,
			funeralHomeId: activeFh.value?.id ?? '',
		}
	}

	/**
	 * Using either the appointmentId or a Session, or both, make a list of contacts
	 * @param appointmentId
	 * @param session
	 */
	const getContacts = async (
		appointmentId: string | null | undefined,
		session: Session | null = null
	): Promise<Array<Contact>> => {
		if (appointmentId === null && session === null) {
			return []
		}

		const contacts: Contact[] = []

		if (!!session) {
			if (
				activeSession.value &&
				activeSession.value.id === session.id &&
				activePlans.value
			) {
				contacts.push(
					...activePlans.value.map<Contact>((plan) => planToContact(plan))
				)
			} else {
				const sessionPlans = await planStore.loadPlans(session.planIds)
				contacts.push(
					...sessionPlans.map<Contact>((plan) => planToContact(plan))
				)
			}
		}

		if (appointmentId) {
			const importer = useFeatureService().isEnabled(
				Feature.SalesforceIntegration
			)
				? new SalesforceAppointmentImporter(appointmentId)
				: new CrmAppointmentImporter(appointmentId)
			const importResult = await importer.import()
			importResult.plans.forEach((plan) => {
				if (!contacts.find((contact) => isSameContact(plan, contact))) {
					contacts.push(planToContact(plan))
				}
			})
		}

		return contacts.filter((contact) => contact.firstName || contact.lastName)
	}

	const getContactsForActiveSession = async () => {
		if (!activeSession.value) {
			throw new Error('No active session to get contacts from!')
		}

		return getContacts(activeSession.value?.appointmentId, activeSession.value)
	}

	return {
		activeSession,
		activePlans,
		activeSlideDeck,
		activeFh,
		activeFhMedia,
		activeFHGroupMedia,
		activeOapProvider,
		activeInsuranceCarrier,
		activePriceBook,
		activePriceBookVersion,
		requestQueue,
		sessions,
		testPriceBook,
		getSessionById,
		getSavedSessionIds,
		getSessionForAppointment,
		loadSession,
		loadAll,
		migrateSessions,
		purge,
		resumeSession,
		saveActiveSession,
		saveSession,
		startSession,
		getPlanHolder,
		getFirstPlanHolderNotConfirmed,
		getSessionFromPlanHolderId,
		getPlanPrototype,
		getContactsForActiveSession,
		getContacts,
	}
})
